import { ref, computed } from '@vue/composition-api'
import calculatorsApi from '@/services/api/calculators'
import {
  ROUND_MONEY_DIVISION,
  ROUND_PLACING,
  FINAL_ROUND_PLACING,
  PLACES_MONEY_DIVISION
} from '@/constants/calculatorRules'

function calcPlacing(money, isFinal, totalCompetitors) {
  if (isFinal) {
    const rule = FINAL_ROUND_PLACING.find(r => totalCompetitors >= r.min && totalCompetitors <= r.max)
    return rule ? rule.value : 6
  }
  const rule = ROUND_PLACING.find(r => money >= r.min && money < r.max)
  return rule ? rule.value : 4
}

function buildPlacesData(money, places) {
  const percentArray = PLACES_MONEY_DIVISION[places] || PLACES_MONEY_DIVISION[4]
  return percentArray.map((pct, idx) => ({
    place: idx + 1,
    percent: pct,
    amount: parseFloat(((money * pct) / 100).toFixed(2))
  }))
}

function buildRound(order, isFinal, totalMoney, percentValue, totalCompetitors) {
  const money = parseFloat(((totalMoney * percentValue) / 100).toFixed(2))
  const places = calcPlacing(money, isFinal, totalCompetitors)

  return {
    id: null,
    order,
    is_final: isFinal,
    money,
    percent: percentValue,
    places,
    placesData: buildPlacesData(money, places),
    editingColumn: null
  }
}

function mapRoundsFromApi(apiRounds) {
  return apiRounds.map(r => ({
    id: r.id,
    order: r.order,
    is_final: r.is_final,
    money: r.sum,
    percent: r.percentages,
    places: r.places,
    placesData: (r.calc_places || []).map(p => ({
      place: p.place,
      percent: p.percentage,
      amount: p.sum
    })),
    editingColumn: null
  }))
}

function mapRoundsToApi(uiRounds) {
  return uiRounds.map(r => ({
    round_order: r.order,
    round_money: r.money,
    round_percent: r.percent,
    round_placing: r.places,
    is_final: r.is_final,
    table_data: (r.placesData || []).map(p => ({
      place: p.place,
      percent: p.percent,
      money: p.amount
    }))
  }))
}

export default function useCalculatorView(initialEventId) {
  let eventId = initialEventId
  const calculatorId = ref(null)
  const draftName = ref('')
  const eventsList = ref([])
  const eventName = ref('')
  const totalNominations = ref(0)
  const totalRounds = ref(0)
  const declaredPrize = ref(0)
  const backAmount = ref(null)
  const rounds = ref([])
  const loading = ref(false)
  const submitting = ref(false)
  const hasCalculated = ref(false)
  const unsaved = ref(false)

  const totalPrize = computed(() => {
    const dec = declaredPrize.value != null ? Number(declaredPrize.value) : 0
    const back = backAmount.value != null ? Number(backAmount.value) : 0
    const competitors = totalNominations.value || 0
    return (dec + (back * competitors)).toFixed(2)
  })

  async function fetchCalculator() {
    loading.value = true
    try {
      const res = await calculatorsApi.getCalculator(eventId)
      const data = res.data.data
      if (data == null) return

      calculatorId.value = data.id != null ? data.id : null
      draftName.value = data.draftName || ''
      eventsList.value = Array.isArray(data.eventsList) ? data.eventsList : []
      eventName.value = data.event != null ? data.event.name : ''
      totalNominations.value = data.totalCompetitors != null ? data.totalCompetitors : 0
      totalRounds.value = data.event != null ? data.event.rounds : 0
      declaredPrize.value = data.event != null ? data.event.declared_prize : 0
      backAmount.value = data.event != null ? data.event.back_amount : null

      if (data.rounds != null && data.rounds.length > 0) {
        rounds.value = mapRoundsFromApi(data.rounds)
        hasCalculated.value = true
      } else {
        hasCalculated.value = false
      }
    } catch (_) { /* http.js handles toast */ } finally {
      loading.value = false
    }
  }

  function calculateRounds() {
    const total = Number(totalPrize.value) || 0
    if (total <= 0) return

    const numRounds = totalRounds.value || 1
    const percentages = ROUND_MONEY_DIVISION[numRounds] || ROUND_MONEY_DIVISION[1]
    const competitors = totalNominations.value || 0

    const newRounds = []
    for (let i = 0; i < numRounds; i++) {
      const isFinal = i === numRounds - 1
      newRounds.push(buildRound(i + 1, isFinal, total, percentages[i], competitors))
    }

    rounds.value = newRounds
    hasCalculated.value = true
    unsaved.value = true
  }

  function onRoundMoneyChange(round) {
    const money = parseFloat(round.money) || 0
    round.money = money

    const total = rounds.value.reduce((s, r) => s + (parseFloat(r.money) || 0), 0)
    const back = backAmount.value != null ? Number(backAmount.value) : 0
    const competitors = totalNominations.value || 0
    declaredPrize.value = parseFloat((total - (back * competitors)).toFixed(2))

    rounds.value.forEach((r) => {
      const t = Number(totalPrize.value) || 0
      r.percent = t > 0 ? parseFloat(((parseFloat(r.money) / t) * 100).toFixed(2)) : 0
      r.places = calcPlacing(r.money, r.is_final, competitors)
      r.placesData = buildPlacesData(r.money, r.places)
    })

    unsaved.value = true
  }

  function onRoundPercentChange(round) {
    const pct = parseFloat(round.percent) || 0
    round.percent = pct

    const total = Number(totalPrize.value) || 0
    round.money = parseFloat(((total * pct) / 100).toFixed(2))

    const competitors = totalNominations.value || 0
    const sumMoney = rounds.value.reduce((s, r) => s + (parseFloat(r.money) || 0), 0)
    const back = backAmount.value != null ? Number(backAmount.value) : 0
    declaredPrize.value = parseFloat((sumMoney - (back * competitors)).toFixed(2))

    rounds.value.forEach((r) => {
      r.places = calcPlacing(r.money, r.is_final, competitors)
      r.placesData = buildPlacesData(r.money, r.places)
    })

    unsaved.value = true
  }

  function onRoundPlacesChange(round, newPlaces) {
    round.places = newPlaces
    round.placesData = buildPlacesData(round.money, round.places)
    unsaved.value = true
  }

  // After a per-place column edit, the round's money is whatever the edited
  // places now sum to, which shifts the overall total — every round's
  // "% of Total" needs to follow, not just the one that was edited.
  function recalcRoundPercentsFromMoney() {
    const total = rounds.value.reduce((s, r) => s + (parseFloat(r.money) || 0), 0)
    const back = backAmount.value != null ? Number(backAmount.value) : 0
    const competitors = totalNominations.value || 0
    declaredPrize.value = parseFloat((total - (back * competitors)).toFixed(2))

    const newTotal = Number(totalPrize.value) || 0
    rounds.value.forEach((r) => {
      r.percent = newTotal > 0 ? parseFloat(((parseFloat(r.money) / newTotal) * 100).toFixed(2)) : 0
    })
  }

  function applyPercentColumnEdit(round) {
    round.placesData.forEach((p) => {
      p.percent = parseFloat(p.percent) || 0
      p.amount = parseFloat(((round.money * p.percent) / 100).toFixed(2))
    })
    round.money = parseFloat(round.placesData.reduce((s, p) => s + p.amount, 0).toFixed(2))
    recalcRoundPercentsFromMoney()
    unsaved.value = true
  }

  function applyMoneyColumnEdit(round) {
    round.placesData.forEach((p) => {
      p.amount = parseFloat(p.amount) || 0
    })
    round.money = parseFloat(round.placesData.reduce((s, p) => s + p.amount, 0).toFixed(2))
    round.placesData.forEach((p) => {
      p.percent = round.money > 0 ? parseFloat(((p.amount / round.money) * 100).toFixed(2)) : 0
    })
    recalcRoundPercentsFromMoney()
    unsaved.value = true
  }

  function toggleColumnEdit(round, column) {
    if (round.editingColumn === column) {
      if (column === 'percent') applyPercentColumnEdit(round)
      if (column === 'money') applyMoneyColumnEdit(round)
      round.editingColumn = null
      return
    }
    if (round.editingColumn != null) return
    round.editingColumn = column
  }

  async function saveCalculator() {
    submitting.value = true
    try {
      const payload = {
        declared_money: declaredPrize.value != null ? Number(declaredPrize.value) : 0,
        back_money: backAmount.value != null ? Number(backAmount.value) : 0,
        total_money: totalPrize.value != null ? Number(totalPrize.value) : 0,
        rounds: mapRoundsToApi(rounds.value)
      }

      // POST only creates the calculator record — rounds data is saved by PATCH.
      if (!calculatorId.value) {
        const created = await calculatorsApi.createCalculator(eventId, {})
        calculatorId.value = created.data.data != null ? created.data.data.id : null
      }
      const res = await calculatorsApi.updateCalculator(calculatorId.value, payload)
      unsaved.value = false
      return { success: true, message: res.data.message }
    } catch (error) {
      const message = error.response?.data?.message || 'A server error occurred. Please try again later.'
      return { success: false, message }
    } finally {
      submitting.value = false
    }
  }

  return {
    calculatorId,
    draftName,
    eventsList,
    eventName,
    totalNominations,
    totalRounds,
    declaredPrize,
    backAmount,
    totalPrize,
    rounds,
    loading,
    submitting,
    hasCalculated,
    unsaved,
    setEventId(newId) { eventId = newId },
    fetchCalculator,
    calculateRounds,
    onRoundMoneyChange,
    onRoundPercentChange,
    onRoundPlacesChange,
    toggleColumnEdit,
    saveCalculator
  }
}



// WEBPACK FOOTER //
// ./src/composables/calculators/useCalculatorView.js